import {
  ETAPAS_XABCDE,
  type AchadoXabcde,
  type AprisionamentoCenario,
  type CenarioExtracao,
  type EtapaXabcde,
  type VeiculoCenarioExtracao,
  type VitimaCenarioExtracao,
} from './cenariosExtracao'
import type { CenarioAutoral, ReferenciaCorrecaoVitima } from './cenariosAutorais'
import type { ProtocoloTriagem } from './protocoloTriagem'

const VERSAO_CENARIO_AUTORAL = 1 as const

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)
const ehString = (valor: unknown): valor is string => typeof valor === 'string'
const ehArrayDe = <T>(valor: unknown, ehItem: (item: unknown) => item is T): valor is readonly T[] =>
  Array.isArray(valor) && valor.every(ehItem)

const etapasXabcdeConhecidas = new Set<string>(ETAPAS_XABCDE)
const regioesConhecidas = new Set(['cabeca', 'torax', 'abdome', 'membroInferior', 'corpoTodo'])
const aprisionamentoTiposConhecidos = new Set(['painel', 'pedais', 'colunaB', 'bancoCinto', 'deformacao'])

const ehAchadoValido = (valor: unknown): valor is AchadoXabcde =>
  ehRegistro(valor) &&
  ehString(valor.resumo) &&
  ehString(valor.detalhe) &&
  (valor.regiao === undefined || regioesConhecidas.has(valor.regiao as string)) &&
  (valor.informado === undefined || typeof valor.informado === 'boolean')

const ehXabcdeValido = (valor: unknown): valor is Readonly<Record<EtapaXabcde, AchadoXabcde>> =>
  ehRegistro(valor) && ETAPAS_XABCDE.every((etapa) => ehAchadoValido(valor[etapa]))

const ehAprisionamentoValido = (valor: unknown): valor is AprisionamentoCenario =>
  ehRegistro(valor) &&
  typeof valor.tipo === 'string' &&
  aprisionamentoTiposConhecidos.has(valor.tipo) &&
  ehString(valor.descricao)

const ehVitimaValida = (valor: unknown): valor is VitimaCenarioExtracao =>
  ehRegistro(valor) &&
  ehString(valor.id) &&
  ehString(valor.veiculoId) &&
  ehString(valor.nome) &&
  ehString(valor.idAncora) &&
  ehString(valor.idPerfil) &&
  (valor.estadoClinico === 'grave' || valor.estadoClinico === 'estavel') &&
  ehArrayDe(valor.perceptivel, ehString) &&
  (valor.urgencia === 'maior' || valor.urgencia === 'estavel' || valor.urgencia === 'avaliar') &&
  (valor.encarceramento === 'mecanico' || valor.encarceramento === 'fisicoI' || valor.encarceramento === 'fisicoII') &&
  ehString(valor.leituraEncarceramento) &&
  ehArrayDe(valor.aprisionamentos, ehAprisionamentoValido) &&
  ehXabcdeValido(valor.xabcde)

const ehVeiculoValido = (valor: unknown): valor is VeiculoCenarioExtracao =>
  ehRegistro(valor) &&
  ehString(valor.id) &&
  ehString(valor.nome) &&
  (valor.configuracao === 'sobreRodas' || valor.configuracao === 'lateral' || valor.configuracao === 'invertido') &&
  (valor.decubito === undefined || valor.decubito === 'ventral' || valor.decubito === 'dorsal') &&
  (valor.sentidoCabeca === undefined || valor.sentidoCabeca === 'frente' || valor.sentidoCabeca === 'traseira') &&
  typeof valor.estabilidadeInicial === 'boolean' &&
  ehArrayDe(valor.riscosIniciais, ehString) &&
  (valor.acessoInicial === 'livre' || valor.acessoInicial === 'limitado' || valor.acessoInicial === 'bloqueado')

const ehCenarioValido = (valor: unknown): valor is CenarioExtracao =>
  ehRegistro(valor) &&
  ehString(valor.id) &&
  ehString(valor.nome) &&
  ehString(valor.resumo) &&
  ehString(valor.objetivo) &&
  ehArrayDe(valor.veiculos, ehVeiculoValido) &&
  ehArrayDe(valor.vitimas, ehVitimaValida) &&
  ehArrayDe(valor.pausas, ehString)

const ehProtocoloValido = (valor: unknown): valor is ProtocoloTriagem =>
  ehRegistro(valor) && ehString(valor.nome) && ehString(valor.versao) && ehString(valor.populacao) && ehString(valor.fonte)

const ehReferenciaValida = (valor: unknown): valor is ReferenciaCorrecaoVitima =>
  ehRegistro(valor) &&
  ehString(valor.vitimaId) &&
  ehArrayDe(
    valor.achadosEssenciais,
    (item): item is EtapaXabcde => typeof item === 'string' && etapasXabcdeConhecidas.has(item),
  ) &&
  ehString(valor.classificacaoEsperada) &&
  ehArrayDe(valor.alternativasAceitas, ehString) &&
  ehString(valor.prioridadesAtencao) &&
  ehString(valor.riscosDeAtraso) &&
  ehString(valor.restricoesGeometricas) &&
  ehString(valor.pontosDeJulgamento)

interface CenarioAutoralSerializado {
  versao: typeof VERSAO_CENARIO_AUTORAL
  id: string
  cenario: CenarioExtracao
  protocolo: ProtocoloTriagem
  referencia: readonly ReferenciaCorrecaoVitima[]
  criadoEm: string
}

/** Versão do aluno: mesmo `cenario` e `protocolo` (metadado, não é resposta) — sem `referencia`. */
interface CenarioAutoralAlunoSerializado {
  versao: typeof VERSAO_CENARIO_AUTORAL
  id: string
  cenario: CenarioExtracao
  protocolo: ProtocoloTriagem
}

export function validarCenarioAutoralSerializado(valor: unknown): valor is CenarioAutoralSerializado {
  if (
    !ehRegistro(valor) ||
    valor.versao !== VERSAO_CENARIO_AUTORAL ||
    !ehString(valor.id) ||
    !ehCenarioValido(valor.cenario) ||
    !ehProtocoloValido(valor.protocolo) ||
    !ehArrayDe(valor.referencia, ehReferenciaValida) ||
    !ehString(valor.criadoEm)
  ) {
    return false
  }
  // cada referência precisa apontar pra uma vítima que de fato existe neste cenário
  const idsVitima = new Set(valor.cenario.vitimas.map((v) => v.id))
  return valor.referencia.every((r) => idsVitima.has(r.vitimaId))
}

export function validarCenarioAutoralAlunoSerializado(valor: unknown): valor is CenarioAutoralAlunoSerializado {
  return (
    ehRegistro(valor) &&
    valor.versao === VERSAO_CENARIO_AUTORAL &&
    ehString(valor.id) &&
    ehCenarioValido(valor.cenario) &&
    ehProtocoloValido(valor.protocolo)
  )
}

export function serializarCenarioAutoralProfessor(autoral: CenarioAutoral): string {
  const snapshot: CenarioAutoralSerializado = {
    versao: VERSAO_CENARIO_AUTORAL,
    id: autoral.id,
    cenario: autoral.cenario,
    protocolo: autoral.protocolo,
    referencia: autoral.referencia,
    criadoEm: autoral.criadoEm,
  }
  if (!validarCenarioAutoralSerializado(snapshot)) {
    throw new Error('Cenário autoral inválido: o snapshot não pôde ser serializado.')
  }
  return JSON.stringify(snapshot, null, 2)
}

/** Nunca inclui `referencia` — é a exportação segura pra distribuir aos alunos. */
export function serializarCenarioAutoralAluno(autoral: CenarioAutoral): string {
  const snapshot: CenarioAutoralAlunoSerializado = {
    versao: VERSAO_CENARIO_AUTORAL,
    id: autoral.id,
    cenario: autoral.cenario,
    protocolo: autoral.protocolo,
  }
  if (!validarCenarioAutoralAlunoSerializado(snapshot)) {
    throw new Error('Cenário autoral inválido: a versão do aluno não pôde ser serializada.')
  }
  return JSON.stringify(snapshot, null, 2)
}

export function desserializarCenarioAutoral(json: string): CenarioAutoral {
  let valor: unknown
  try {
    valor = JSON.parse(json) as unknown
  } catch {
    throw new Error('Cenário autoral inválido: o conteúdo não é um JSON válido.')
  }
  if (!validarCenarioAutoralSerializado(valor)) {
    throw new Error('Cenário autoral inválido: estrutura ou valores não reconhecidos.')
  }
  return { id: valor.id, cenario: valor.cenario, protocolo: valor.protocolo, referencia: valor.referencia, criadoEm: valor.criadoEm }
}

export function desserializarCenarioAutoralAluno(
  json: string,
): { id: string; cenario: CenarioExtracao; protocolo: ProtocoloTriagem } {
  let valor: unknown
  try {
    valor = JSON.parse(json) as unknown
  } catch {
    throw new Error('Cenário autoral inválido: o conteúdo não é um JSON válido.')
  }
  if (!validarCenarioAutoralAlunoSerializado(valor)) {
    throw new Error('Cenário autoral inválido: estrutura ou valores não reconhecidos.')
  }
  return { id: valor.id, cenario: valor.cenario, protocolo: valor.protocolo }
}
